import type { Caller } from "@repo/common";
import { callersCollection, db } from "@repo/ui";
import { doc, onSnapshot } from "firebase/firestore";
import { useEffect, useState } from "react";
import { callerDate } from "../utils/callerDirectory";

// Undefined selects the directory; a supplied ID subscribes to only that document.
export function useCallers(companyId?: string, callerId?: string) {
	const [state, setState] = useState<{
		key: string;
		callers: Caller[];
		loading: boolean;
		error: boolean;
	}>({ key: "", callers: [], loading: true, error: false });
	const key = JSON.stringify([companyId, callerId]);
	useEffect(() => {
		if (!companyId) return;
		let active = true;
		setState({ key, callers: [], loading: true, error: false });
		const ref = callersCollection(db, companyId);
		const receive = (docs: Array<{ id: string; data: () => Caller }>) => {
			if (!active) return;
			setState({
				key,
				loading: false,
				error: false,
				callers: docs.map((d) => {
					const data = d.data();
					return {
						...data,
						id: d.id,
						firstCallAt: callerDate(data.firstCallAt),
						lastCallAt: callerDate(data.lastCallAt),
					};
				}),
			});
		};
		const failure = () => {
			if (active) setState({ key, callers: [], loading: false, error: true });
		};
		const unsubscribe =
			callerId === undefined
				? onSnapshot(ref, (snap) => receive(snap.docs), failure)
				: onSnapshot(
						doc(ref, callerId),
						(snap) => receive(snap.exists() ? [snap] : []),
						failure,
					);
		return () => {
			active = false;
			unsubscribe();
		};
	}, [companyId, callerId, key]);
	return state.key === key
		? state
		: { callers: [], loading: !!companyId, error: false };
}
